// Pure env → MSAL-config mapping for the Entra External ID cutover (ADR 0017).
//
// Kept free of any @azure/msal import so it stays unit-testable without
// instantiating a PublicClientApplication (which needs a browser crypto env).
//
// The whole cutover from the workforce `common` authority (Microsoft account
// required) to Entra External ID / CIAM (any email or social IdP) is
// values-only: set VITE_ENTRA_KNOWN_AUTHORITY to the `<tenant>.ciamlogin.com`
// host and the mapping below flips to External ID mode. With it unset,
// everything resolves exactly as the current Microsoft-account flow — so this
// is inert until the External ID env values are supplied.

export interface AuthEnv {
  VITE_ENTRA_CLIENT_ID?: string;
  VITE_ENTRA_AUTHORITY?: string;
  /** `<tenant>.ciamlogin.com`. Presence is what switches us to External ID. */
  VITE_ENTRA_KNOWN_AUTHORITY?: string;
  /** External-ID-era scope var; supersedes VITE_API_SCOPE. */
  VITE_ENTRA_API_SCOPE?: string;
  /** Legacy scope var — kept as a fallback so the current env keeps working. */
  VITE_API_SCOPE?: string;
}

/** True once an External ID (CIAM) authority host is configured. */
export function isExternalId(env: AuthEnv): boolean {
  return Boolean(env.VITE_ENTRA_KNOWN_AUTHORITY?.trim());
}

/**
 * CIAM requires its authority host listed in knownAuthorities; the workforce
 * `common` authority must NOT list one (MSAL knows login.microsoftonline.com
 * natively). Empty array when unset ⇒ no change to the current flow.
 */
export function resolveKnownAuthorities(env: AuthEnv): string[] {
  const host = env.VITE_ENTRA_KNOWN_AUTHORITY?.trim();
  return host ? [host] : [];
}

/**
 * Prefer the External-ID-era scope var; fall back to the legacy VITE_API_SCOPE
 * so the current production env keeps working until it's renamed at cutover.
 */
export function resolveApiScope(env: AuthEnv): string {
  return env.VITE_ENTRA_API_SCOPE?.trim() || env.VITE_API_SCOPE?.trim() || '';
}

export interface LoginRequest {
  scopes: string[];
  prompt?: 'select_account';
}

/**
 * `prompt: 'select_account'` forces the Microsoft account picker on every
 * login — a shared-workforce-device pattern (ADR 0004) that is wrong for
 * self-service consumer signup. Drop it once External ID is configured; keep
 * it on the current workforce flow so this change stays inert until cutover.
 */
export function buildLoginRequest(env: AuthEnv): LoginRequest {
  const req: LoginRequest = { scopes: [resolveApiScope(env)] };
  if (!isExternalId(env)) req.prompt = 'select_account';
  return req;
}
